// react
import { Dispatch, MouseEvent, SetStateAction, useMemo, useState } from 'react';

// types
import {
	Account,
	AccountEntity,
	Entity,
	EntityPayload,
	EntityType,
	ErrorCause,
	Feedback,
	Loan,
	LoanPayload,
	Option,
	Project,
	ProjectPayload,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import {
	Alert,
	AlertTitle,
	Box,
	Checkbox as MuiCheckbox,
	Chip,
	Grid,
	InputLabel,
	SxProps,
	Typography,
} from '@mui/material';

// fields
import { Checkbox } from '../../fields/Checkbox/Checkbox';
import { Select } from '../../fields/Select/Select';

// forms
import { EntityOnboard } from '../FormOnboardProject/EntityOnboard/EntityOnboard';

// props
export interface FormOnboardLoanProps {
	loan: Loan;
	onClose: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement | HTMLFormElement>) => void;
	onSuccess: (e: MouseEvent<HTMLFormElement>, project: Project) => void;
	project: ProjectPayload;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormOnboardLoan = ({
	loan,
	onClose,
	onSuccess,
	project,
	setIsLoading,
}: FormOnboardLoanProps) => {
	// hooks
	const { t } = useTranslation();

	// Project is already onboarded, so use the loan data here rather than context values
	const { accounts = [], entities = [], ...restLoan } = loan;
	const entitiesPending = entities as unknown as EntityPayload[];

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [accountsExisting, setAccountsExisting] = useState<Account[]>([]);
	const [entitiesExisting, setEntitiesExisting] = useState<Entity[]>([]);
	const [entityIdsExpanded, setEntityIdsExpanded] = useState<Set<string>>(new Set());
	const [entityIdsSelected, setEntityIdsSelected] = useState<Set<string>>(
		new Set(entitiesPending.map((entity, index) => (entity.idWbg ? entity.idWbg : `idx-${index}`)))
	);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [selectedUsersByEntity, setSelectedUsersByEntity] = useState<Map<string, Set<string>>>(
		() => {
			const map = new Map<string, Set<string>>();
			// Initialize with all users selected for each entity (default behavior)
			entitiesPending.forEach((entity) => {
				if (entity.idWbg && entity.users) {
					const userIds = entity.users.map((user) => user.idWbg).filter((id): id is string => !!id);
					map.set(entity.idWbg, new Set(userIds));
				}
			});
			return map;
		}
	);

	// fetch existing entities
	useFetch({
		isEnabled: true,
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project.id}&type=PIU&fields=id,idWbg,name,type`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				// set existing entities
				setEntitiesExisting(res.data);
			}
		},
	});

	// fetch existing accounts
	useFetch({
		isEnabled: true,
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project.id}&populate=entities&fields=id,idWbg,name,entities`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setAccountsExisting(res.data);
			}
		},
	});

	// handlers
	const handleClickExpand = (entityId: string) => {
		setEntityIdsExpanded((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(entityId)) {
				newSet.delete(entityId);
			} else {
				newSet.add(entityId);
			}
			return newSet;
		});
	};

	const handleToggleAllEntities = (checked: boolean) => {
		if (!entitiesPending) return;
		if (checked) {
			const allIds = new Set(
				entitiesPending.map((entity, index) => (entity.idWbg ? entity.idWbg : `idx-${index}`))
			);
			setEntityIdsSelected(allIds);
		} else {
			setEntityIdsSelected(new Set());
			setSelectedUsersByEntity(new Map());
		}
	};

	const handleToggleAllUsers = (entity: EntityPayload, checked: boolean) => {
		const users = entity.users || [];
		setSelectedUsersByEntity((prev) => {
			const map = new Map(prev);
			if (checked) {
				const allUsers = new Set(users.map((user) => String(user.idWbg)).filter(Boolean));
				map.set(entity.idWbg || '', allUsers);
			} else {
				map.set(entity.idWbg || '', new Set());
			}
			return map;
		});
	};

	// define selected entities with selected users
	const entitiesSelected = entitiesPending
		.filter((e) => e.idWbg && entityIdsSelected.has(e.idWbg))
		.map((entity) => {
			// Include only the selected users for this entity
			const selectedUserIds = selectedUsersByEntity.get(entity.idWbg || '') || new Set();
			const selectedUsers =
				entity.users?.filter((user) => {
					return typeof user.idWbg === 'string' && selectedUserIds.has(user.idWbg);
				}) || [];

			return {
				...entity,
				users: selectedUsers,
			};
		});

	// vars
	const idForm = 'form-onboard-loan';

	// For new accounts, only entities associated with this loan are valid owners
	const optionsEntities: Option[] = useMemo(
		() =>
			entitiesPending
				.filter((entity) => !entity.type || entity.type === 'PIU')
				.map((entity) => ({
					label: entity.name,
					value: entity.idWbg as string,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[entitiesPending]
	);

	const hasEntities = entitiesPending.length > 0;

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// start loader
		setIsLoading(true);

		// hide feedback
		setFeedback(null);

		try {
			const form = document.getElementById(idForm) as HTMLFormElement;
			const fd = new FormData(form);
			const entries = Object.fromEntries(fd.entries());

			// for each selected entity
			for (let e = 0; e < entitiesSelected.length; e++) {
				// define entity
				const entity = entitiesSelected[e];
				const entityExisting = entitiesExisting.find((ent) => ent.idWbg === entity.idWbg);

				// check if the users have required fields
				if (!entityExisting && entity.users) {
					for (const user of entity.users) {
						// check if first name exists
						if (!user.firstName) {
							throw new Error(`The user with the email ${user.email} is missing a first name.`, {
								cause: {
									id: 'USER_FIRST_NAME_NON_EXISTENT',
									details: {
										email: user.email,
										entity: entity.name,
									},
								},
							});
						}

						// check if last name exists
						if (!user.lastName) {
							throw new Error(`The user with the email ${user.email} is missing a last name.`, {
								cause: {
									id: 'USER_LAST_NAME_NON_EXISTENT',
									details: {
										email: user.email,
										entity: entity.name,
									},
								},
							});
						}

						// check if email exists
						if (!user.email) {
							const userFullName = `${user.firstName} ${user.lastName}`;

							throw new Error(`The user ${userFullName} is missing an email.`, {
								cause: {
									id: 'USER_EMAIL_NON_EXISTENT',
									details: {
										name: userFullName,
										entity: entity.name,
									},
								},
							});
						}
					}
				}
			}

			// add owner to accounts
			const payloadAccounts = accounts.map((account) => {
				const accountExisting = accountsExisting.find(
					(a) => String(a.idWbg) === String(account.idWbg)
				);

				if (accountExisting) {
					// Start with entities already on the account in FundsChain
					const existingIdWbgs = new Set(
						accountExisting.entities.map((ae) => String((ae.entity as unknown as Entity).idWbg))
					);
					const entitiesMerged = [
						...accountExisting.entities.map((ae) => ({
							idWbg: String((ae.entity as unknown as Entity).idWbg),
							relationship: ae.relationship,
						})),
						// Add any entities from CC not yet on the account
						...account.entities
							.filter((ent) => !existingIdWbgs.has(String((ent.entity as unknown as Entity).idWbg)))
							.map((ent) => ({
								idWbg: String((ent.entity as unknown as Entity).idWbg),
								relationship: ent.relationship,
							})),
					];

					return {
						...account,
						entities: entitiesMerged,
					};
				}

				return {
					...account,
					entities: [
						{
							idWbg: entries[`account-${account.idWbg}-owner`] as string,
							relationship: 'OWNER',
						},
					],
					status: 'ACTIVE',
				};
			});

			// add type to entities
			const payloadEntities = entitiesSelected.map((entity) => {
				const entityExisting = entitiesExisting.find((ent) => ent.idWbg === entity.idWbg);

				return {
					...entity,
					type: entityExisting?.type || (entries[`entity-${entity.idWbg}-type`] as EntityType),
				};
			});

			const payloadLoans: Partial<LoanPayload>[] = [restLoan].map((loan) => {
				return {
					amount: loan.amount,
					code: loan.code,
					currency: loan.currency,
					dateApproved: loan.dateApproved,
					dateClosing: loan.dateClosing,
					dateEffective: loan.dateEffective,
					dateSigning: loan.dateSigning,
					disbursed: loan.disbursed,
					financier: loan.financier,
					name: loan.name,
					num: loan.num,
					status: entries.isEnabled ? 'ENABLED' : loan.status,
					subLoanNum: loan.subLoanNum,
				};
			});

			// post onboarding
			const fetchOnboardig = await fetch(`${process.env.REACT_APP_API_URL}/onboard/loan`, {
				method: 'post',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					accounts: payloadAccounts,
					entities: payloadEntities,
					loans: payloadLoans,
					project: project.id,
				}),
			});

			const resOnboarding = await fetchOnboardig.json();

			if (resOnboarding.error) {
				throw new Error(resOnboarding.error.message, {
					cause: {
						id: resOnboarding.error.id,
						details: resOnboarding.error.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess(e, resOnboarding);
			}

			if (onClose) {
				onClose(e);
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				switch (cause.id) {
					case 'LOAN_DUPLICATE':
						feedbackNew.alert.children = t('error.loanDuplicate', {
							financier: cause.details.financier,
							num: cause.details.num,
						});
						break;

					case 'LOAN_ENTITY_DUPLICATE':
						feedbackNew.alert.children = t('error.loanEntityDuplicate');
						break;

					case 'PROJECT_LOAN_DUPLICATE':
						feedbackNew.alert.children = t('error.projectLoanDuplicate');
						break;

					case 'USER_DUPLICATE':
						feedbackNew.alert.children = t('error.userDuplicate', { email: cause.details.email });
						break;

					case 'USER_EMAIL_NON_EXISTENT':
						feedbackNew.alert.children = t('error.userEmailNonExistent', {
							entity: cause.details.entity,
							name: cause.details.name,
						});
						break;

					case 'USER_FIRST_NAME_NON_EXISTENT':
						feedbackNew.alert.children = t('error.userFirstNameNonExistent', {
							email: cause.details.email,
							entity: cause.details.entity,
						});
						break;

					case 'USER_LAST_NAME_NON_EXISTENT':
						feedbackNew.alert.children = t('error.userLastNameNonExistent', {
							email: cause.details.email,
							entity: cause.details.entity,
						});
						break;

					default:
						feedbackNew.alert.children = t('error.server');
				}
			}

			setFeedback(feedbackNew);
		} finally {
			// end loader
			setIsLoading(false);
		}
	};

	return (
		<form id={idForm} onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography fontWeight={600}>{t('entities')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Box
						sx={(theme) => {
							return {
								borderColor: theme.palette.divider,
								borderRadius: 2,
								borderStyle: 'solid',
								borderWidth: 1,
								overflow: 'hidden',
							};
						}}>
						<Grid container>
							<Grid item xs={12}>
								<Box
									sx={(theme) => {
										return {
											backgroundColor: theme.palette.brand.grey[100],
											borderBottomColor: theme.palette.divider,
											borderBottomStyle: 'solid',
											borderBottomWidth: 1,
											padding: theme.spacing(2),
										};
									}}>
									<Grid alignItems="center" container spacing={2}>
										<Grid item xs={6}>
											<Grid alignItems="center" container spacing={1}>
												<Grid item alignItems="center" display="flex">
													<MuiCheckbox
														checked={entityIdsSelected.size === entitiesPending.length}
														id={`${idForm}-select-all-entities`}
														onChange={(e) => handleToggleAllEntities(e.target.checked)}
														sx={{ p: 0 }}
													/>
												</Grid>
												<Grid item flex={1}>
													<InputLabel
														htmlFor={`${idForm}-select-all-entities`}
														sx={{ fontWeight: 600, m: 0 }}>
														{t('entity')}
													</InputLabel>
												</Grid>
											</Grid>
										</Grid>
										<Grid item xs={6}>
											<Typography fontWeight={600}>{t('type')}</Typography>
										</Grid>
									</Grid>
								</Box>
							</Grid>
							{entitiesPending.length > 0 ? (
								entitiesPending
									.sort((a, b) => (a.name > b.name ? 1 : -1))
									.map((entity, indexEntities) => {
										const entityExisting = entitiesExisting.find(
											(ent) => ent.idWbg === entity.idWbg
										);
										const isLast = indexEntities === entities.length - 1;

										let feedback: Feedback | null = null;

										if (entityExisting) {
											feedback = {
												alert: {
													children: t('alert.entityAlreadyOnboarded'),
													severity: 'info',
												},
											};
										}

										// Check if entity is selected using the Set
										const isChecked = entity.idWbg ? entityIdsSelected.has(entity.idWbg) : false;

										return (
											<Grid item key={`${entity.idWbg}-${indexEntities}`} xs={12}>
												<Box
													sx={(theme) => {
														let sx: SxProps = {};

														if (!isLast) {
															sx = {
																...sx,
																borderBottomColor: theme.palette.divider,
																borderBottomStyle: 'solid',
																borderBottomWidth: 1,
															};
														}

														return sx;
													}}>
													<EntityOnboard
														defaultValue={entityExisting?.type || 'PIU'}
														entity={entity}
														feedback={feedback}
														isChecked={isChecked}
														isExpanded={entityIdsExpanded.has(entity.idWbg || '')}
														isNew={!entityExisting}
														onChangeAllUsers={handleToggleAllUsers}
														onChangeEntity={(e, checked, entity) => {
															if (!entity.idWbg) return;

															// add or remove selected entity ID
															const newSelectedIds = new Set(entityIdsSelected);
															if (checked) {
																newSelectedIds.add(entity.idWbg);
															} else {
																newSelectedIds.delete(entity.idWbg);
															}

															setEntityIdsSelected(newSelectedIds);

															// Update user selections for this entity
															const newSelectedUsersByEntity = new Map(selectedUsersByEntity);
															if (checked) {
																// Select all users for this entity
																const allUserIds =
																	entity.users
																		?.map((user) => user.idWbg)
																		.filter((id): id is string => !!id) || [];
																newSelectedUsersByEntity.set(entity.idWbg, new Set(allUserIds));
															} else {
																// Deselect all users for this entity
																newSelectedUsersByEntity.set(entity.idWbg, new Set());
															}
															setSelectedUsersByEntity(newSelectedUsersByEntity);
														}}
														onChangeUser={(e, checked, user, entity) => {
															if (!entity.idWbg || typeof user.idWbg !== 'string') return;

															// Update the selectedUsersByEntity map
															const newSelectedUsersByEntity = new Map(selectedUsersByEntity);
															const entityUserIds =
																newSelectedUsersByEntity.get(entity.idWbg) || new Set();
															const newEntityUserIds = new Set(entityUserIds);

															if (checked) {
																newEntityUserIds.add(user.idWbg);
															} else {
																newEntityUserIds.delete(user.idWbg);
															}

															newSelectedUsersByEntity.set(entity.idWbg, newEntityUserIds);
															setSelectedUsersByEntity(newSelectedUsersByEntity);
														}}
														onClickExpand={handleClickExpand}
														selectedUserIds={
															selectedUsersByEntity.get(entity.idWbg || '') || new Set()
														}
													/>
												</Box>
											</Grid>
										);
									})
							) : (
								<Grid item xs={12}>
									<Alert
										severity="info"
										sx={(theme) => {
											return {
												borderRadius: 0,
											};
										}}>
										{t('alert.loanNoEntities')}
									</Alert>
								</Grid>
							)}
						</Grid>
					</Box>
				</Grid>
				<Grid item xs={12}>
					<Typography fontWeight={600}>{t('accounts')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Box
						sx={(theme) => {
							return {
								border: `1px ${theme.palette.divider} solid`,
								borderRadius: 2,
								overflow: 'hidden',
							};
						}}>
						<Grid container>
							<Grid item xs={12}>
								<Box
									sx={(theme) => {
										return {
											backgroundColor: theme.palette.brand.grey[100],
											borderBottom: `1px ${theme.palette.divider} solid`,
											padding: theme.spacing(2),
											minWidth: '100%',
										};
									}}>
									<Grid alignItems="center" container spacing={2}>
										<Grid item xs={6}>
											<Grid alignItems="center" container spacing={1}>
												<Grid item flex={1}>
													<Typography fontWeight={600}>{t('account')}</Typography>
												</Grid>
											</Grid>
										</Grid>
										<Grid item xs={6}>
											<Typography fontWeight={600}>{t('owner')}</Typography>
										</Grid>
									</Grid>
								</Box>
							</Grid>
							{accounts.length > 0 ? (
								accounts.map((account, indexAccounts) => {
									const accountExisting = accountsExisting.find(
										(a) => String(a.idWbg) === String(account.idWbg)
									);
									const isLast = indexAccounts === accounts.length - 1;

									let entitiesNotInLoan: AccountEntity[] = [];
									let optionsOwner: Option[] = optionsEntities;

									// Find  owner entity for existing account if it exists
									const ownerEntry = accountExisting?.entities.find(
										(ae) => ae.relationship === 'OWNER'
									);
									const entityOwner = ownerEntry?.entity as unknown as Entity | undefined;

									// If account exists
									if (accountExisting) {
										// Redefine owner options
										if (entityOwner) {
											optionsOwner = [
												{
													label: entityOwner.name,
													value: String(entityOwner.idWbg),
												},
											];
										}

										// Redefine entities not in loan
										entitiesNotInLoan = accountExisting.entities.filter((ae) => {
											const entity = ae.entity as unknown as Entity;
											return !entitiesPending.some((ep) => ep.idWbg === String(entity.idWbg));
										});
									}

									return (
										<Grid item key={`project-account-${account.idWbg}-${indexAccounts}`} xs={12}>
											<Box
												sx={(theme) => {
													const styles: SxProps = {
														padding: theme.spacing(2),
														minWidth: '100%',
													};

													if (!isLast) {
														styles.borderBottom = `1px ${theme.palette.divider} solid`;
													}
													return styles;
												}}>
												<Grid alignItems="flex-start" container spacing={2}>
													<Grid item xs={6}>
														<Grid alignItems="center" container spacing={1}>
															<Grid item>
																<Typography fontWeight={600}>{account.name}</Typography>
															</Grid>
															{!accountExisting && (
																<Grid item>
																	<Chip
																		color="success"
																		label={t('new')}
																		size="small"
																		variant="twoTone"
																	/>
																</Grid>
															)}
														</Grid>
													</Grid>
													<Grid item xs={6}>
														<Grid container spacing={1}>
															<Grid item xs={12}>
																<Select
																	defaultValue={entityOwner ? String(entityOwner.idWbg) : undefined}
																	disabled={!!accountExisting || !hasEntities}
																	hasNoneOption={false}
																	id={`${idForm}-account-${account.idWbg}-owner`}
																	name={`account-${account.idWbg}-owner`}
																	options={optionsOwner}
																	required={!accountExisting}
																/>
															</Grid>
															{entitiesNotInLoan.map((ae) => {
																const entity = ae.entity as unknown as Entity;
																const key =
																	ae.relationship === 'OWNER'
																		? 'alert.accountEntityOwnerNotAssociatedToLoan'
																		: 'alert.accountEntityAssociateNotAssociatedToLoan';
																return (
																	<Grid item key={entity.id} xs={12}>
																		<Alert severity="warning">
																			{t(key, {
																				account: account.name,
																				name: entity.name,
																			})}
																		</Alert>
																	</Grid>
																);
															})}
														</Grid>
													</Grid>
												</Grid>
											</Box>
										</Grid>
									);
								})
							) : (
								<Grid item xs={12}>
									<Alert
										severity="info"
										sx={(theme) => {
											return {
												borderRadius: 0,
											};
										}}>
										{t('alert.projectNoAccounts')}
									</Alert>
								</Grid>
							)}
						</Grid>
					</Box>
				</Grid>
				<Grid item xs={12}>
					<Checkbox
						defaultChecked={true}
						id={`${idForm}-is-onboarded`}
						label={t('enableLoan')}
						name="isEnabled"
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
